/**
 * Agency Dashboard Tour Configuration
 * Introduces agency owners and admins to agency management features
 */

import { TourDefinition } from './tour.types';

export const agencyDashboardTour: TourDefinition = {
  id: 'agency-dashboard-tour',
  name: 'Agency Dashboard',
  page: '/agency-dashboard',
  version: 12, // Updated with analytics tabs and scroll behavior
  requiresAgency: true, // Only for agency owners/admins
  allowedAgencyRoles: ['owner', 'admin', 'agency_owner'],
  nextTourId: 'leads-tour', // Chain to leads tour
  steps: [
    {
      id: 'agency-intro',
      targetSelector: '[data-tour="agency-header"]',
      title: 'Your Agency Dashboard',
      description:
        'Welcome to your agency command center! Track team performance, manage agents, and see agency-wide analytics all in one place.',
      placement: 'bottom',
    },
    {
      id: 'agency-metrics',
      targetSelector: '[data-tour="agency-metrics"]',
      title: 'Agency Performance',
      description:
        'View key metrics for your entire agency - total premium sold, policies, closing ratio, and more across all team members.',
      placement: 'bottom',
    },
    {
      id: 'agency-tabs',
      targetSelector: '[data-tour="agency-tabs"]',
      title: 'Agency Management Tabs',
      description:
        'Navigate between different views: Manage Agency settings, view Sub-Agencies, and manage your Agency Agents. Tip: Control agent export permissions and leaderboard visibility in Account Settings > Preferences.',
      placement: 'auto',
    },
    {
      id: 'sub-agencies-tab',
      targetSelector: '[data-tour="agency-tab-subagencies"]',
      title: 'Sub-Agencies',
      description:
        'This tab shows all your sub-agencies. Create new sub-agencies to organize your team structure and track their performance.',
      placement: 'bottom',
      onShow: () => {
        // Click the sub-agencies tab to show its content
        // Use setTimeout to ensure element is ready and dispatch proper events for Radix UI
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="agency-tab-subagencies"]') as HTMLElement;
          if (tab) {
            // Radix UI requires proper event dispatching
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'sub-agencies-content',
      targetSelector: '[data-tour="sub-agencies-content"]',
      title: 'Sub-Agency List',
      description:
        'Here you can view all your sub-agencies, see their performance metrics, and click to view detailed information about each one.',
      placement: 'top',
    },
    {
      id: 'agents-tab',
      targetSelector: '[data-tour="agency-tab-agents"]',
      title: 'Agency Agents',
      description:
        'This tab shows all agents in your agency. Manage their roles, view their performance, and invite new team members.',
      placement: 'bottom',
      onShow: () => {
        // Click the agents tab to show its content
        // Use setTimeout to ensure element is ready and dispatch proper events for Radix UI
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="agency-tab-agents"]') as HTMLElement;
          if (tab) {
            // Radix UI requires proper event dispatching
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'agent-list',
      targetSelector: '[data-tour="agent-list"]',
      title: 'Your Team',
      description:
        'View all agents in your agency. See their status, recent activity, and individual performance metrics. Click on an agent to see detailed stats.',
      placement: 'top',
    },
    {
      id: 'invite-agents',
      targetSelector: '[data-tour="invite-agents"]',
      title: 'Invite Agents',
      description:
        'Grow your team! Click here to invite new agents to join your agency. They\'ll receive an email invitation to get started.',
      placement: 'left',
    },
    {
      id: 'agent-actions',
      targetSelector: '[data-tour="agent-actions"]',
      title: 'Agent Management',
      description:
        'For each agent, you can: View their detailed performance, Edit their role (make them a Manager), or Remove them from your agency.',
      placement: 'left',
    },
    {
      id: 'analytics-tabs',
      targetSelector: '[data-tour="agency-analytics-tabs"]',
      title: 'Analytics Section',
      description:
        'Dive deeper into your agency performance with these analytics tabs. View premium by carrier, top performers, and performance trends.',
      placement: 'top',
      onShow: () => {
        // Scroll analytics section into view
        setTimeout(() => {
          const analyticsSection = document.querySelector('[data-tour="agency-analytics-tabs"]');
          if (analyticsSection) {
            analyticsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      },
    },
    {
      id: 'premium-sold-tab',
      targetSelector: '[data-tour="agency-tab-premium"]',
      title: 'Premium Sold by Carrier',
      description:
        'See a breakdown of all premium sold by carrier. Quickly identify which carriers are generating the most business.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="agency-tab-premium"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'top-performers-tab',
      targetSelector: '[data-tour="agency-tab-performers"]',
      title: 'Top Performers',
      description:
        'Recognize your best agents! This tab shows the top performing agents across all agencies you manage.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="agency-tab-performers"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'trends-tab',
      targetSelector: '[data-tour="agency-tab-trends"]',
      title: 'Performance Trends',
      description:
        'Track your agency\'s growth over time. View monthly trends for premium, policies, and more to spot patterns and opportunities.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="agency-tab-trends"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
  ],
};
